// Geometry + layout for the org chart canvas. Kept free of React so it can be
// exercised directly: node src/pages/DashboardV2/utils/orgChartLayout.check.mjs

// Card + spacing geometry. The tidy-tree pass is written against these, so
// changing a size here re-spaces the whole chart. Cards are kept narrow and rows
// tall on purpose: a wide org runs away horizontally long before it runs out of
// vertical room, so width is the scarce axis.
export const NODE_W = 204;
export const NODE_H = 84;
export const H_GAP = 18;
export const V_GAP = 96;
export const ROW_H = NODE_H + V_GAP;

/**
 * Tidy-tree layout in one pass.
 *
 * X: post-order. Leaves (and collapsed nodes) each consume a slot, and every
 * parent centres itself over its children. Only expanded nodes are walked, which
 * is what keeps a 5000-person org cheap — a collapsed chart is a few dozen cards
 * no matter how big the company is.
 *
 * Y: the person's level in the org's team structure, not their depth in the
 * reporting tree. Everyone on Level 3 lands on the same horizontal line whether
 * they report to a Level 1 or a Level 2, which is what makes seniority readable
 * across branches.
 *
 * @param {Array} roots  hierarchy roots, each { id, level, reports: [...] }
 * @param {(id: string) => boolean} isOpen
 * @returns {{ nodes: Array, edges: Array }} React Flow nodes/edges
 */
export function layoutTree(roots, isOpen) {
  const nodes = [];
  const edges = [];
  let cursor = 0;

  // Rows are absolute level indexes, so a chart rooted partway down the org
  // (anyone who isn't an admin) would otherwise open with empty rows above it.
  const baseRow = roots.length ? Math.min(...roots.map((r) => r.level ?? 0)) : 0;
  const rowY = (row) => (row - baseRow) * ROW_H;

  // Levels mean a parent and child can be several rows apart, and an edge drawn
  // straight across that span would put its horizontal run right through the
  // cards on the rows in between. So every long edge is broken into one hop per
  // row through invisible joints, the standard layered-drawing trick: each hop
  // turns only inside the gap between two rows, and the joint reserves the
  // corridor it passes through. Every segment is then either a short elbow under
  // a parent or a straight vertical drop, and no line can touch a card.
  const link = (parentId, parentRow, childId, childRow, childX) => {
    let from = parentId;
    for (let row = parentRow + 1; row < childRow; row++) {
      const jointId = `${parentId}~${childId}@${row}`;
      nodes.push({
        id: jointId,
        type: "orgJoint",
        // 1x1, centred on the child's column so the drop stays plumb, and sat on
        // the row's card line so a hop into a joint has the same geometry as a
        // hop into a card. That makes every sibling turn at the same height, so
        // they share one bus line instead of drawing several near-parallel ones.
        position: { x: childX + NODE_W / 2, y: rowY(row) },
        width: 1,
        height: 1,
        data: { row },
        draggable: false,
        selectable: false,
      });
      edges.push({ id: `${from}->${jointId}`, source: from, target: jointId, type: "smoothstep" });
      from = jointId;
    }
    edges.push({ id: `${from}->${childId}`, source: from, target: childId, type: "smoothstep" });
  };

  const walk = (node, parentRow, parentId) => {
    // A child must always sit below its parent. Positions whose levels disagree
    // with the reporting line (a manager reporting to a peer, or a position
    // missing from the team structure) get pushed down one row instead of
    // colliding with their own parent.
    const row = parentRow == null
      ? (node.level ?? baseRow)
      : Math.max(node.level ?? parentRow + 1, parentRow + 1);

    const open = isOpen(node.id) && node.reports.length > 0;
    let x;
    if (!open) {
      x = cursor;
      cursor += NODE_W + H_GAP;
    } else {
      const childXs = node.reports.map((child) => walk(child, row, node.id));
      x = (childXs[0] + childXs[childXs.length - 1]) / 2;
    }

    nodes.push({
      id: node.id,
      type: "orgCard",
      position: { x, y: rowY(row) },
      // Declared up front so viewport culling is correct on the very first frame,
      // instead of after the canvas has measured every card.
      width: NODE_W,
      height: NODE_H,
      data: { node, open, row },
      draggable: false,
    });

    if (parentId) link(parentId, parentRow, node.id, row, x);
    return x;
  };

  roots.forEach((root) => {
    walk(root, null, null);
    cursor += NODE_W; // breathing room between separate reporting trees
  });

  return { nodes, edges };
}

/**
 * Flat index of the tree: id -> { node, parentId, depth }. Powers search, the
 * detail drawer, and expanding the path down to a searched person.
 */
export function indexTree(roots) {
  const index = new Map();
  const walk = (node, parentId, depth) => {
    index.set(node.id, { node, parentId, depth });
    node.reports.forEach((child) => walk(child, node.id, depth + 1));
  };
  roots.forEach((r) => walk(r, null, 0));
  return index;
}
